import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Pencil, Plus, Trash2 } from "lucide-react";
import { Button, Card, cx, DurationInput, Field, IconButton, Input, NumberStepper, Segmented, TextField, Toggle } from "@/components/ui";
import { Avatar, kidColor, Points } from "@/components/bits";
import { EmojiAvatarPicker } from "@/components/EmojiPicker";
import { api } from "@/lib/api";
import { useAction, useRewardTypes } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { ConsentCard } from "./Privacy";
import { useInstall } from "@/lib/pwa";
import { AVATARS, COLORS, CURRENCY_PRESETS, JOB_TEMPLATES, RESPONSIBILITY_TEMPLATES } from "@shared/templates";
import { quickPrice } from "@shared/pricing";
import { NOT_SCHOOL_NIGHTS, ALL_DAYS } from "@shared/time";
import { isValidPin } from "@shared/pin";
import type { MeDTO, MemberDTO } from "@shared/types";

const STEPS = ["Currency", "Kids", "Responsibilities", "Jobs", "Rules", "Ready"];

function StepShell({
  step,
  title,
  subtitle,
  children,
  onBack,
  onNext,
  nextLabel = "Continue",
  nextDisabled,
  loading,
  skip,
}: {
  step: number;
  title: string;
  subtitle: string;
  children: React.ReactNode;
  onBack?: () => void;
  onNext: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  loading?: boolean;
  skip?: () => void;
}) {
  return (
    <div className="mx-auto flex min-h-dvh max-w-xl flex-col px-5 pb-[calc(env(safe-area-inset-bottom)+20px)] pt-[calc(env(safe-area-inset-top)+16px)]">
      <div className="flex items-center gap-2">
        {onBack ? (
          <button type="button" onClick={onBack} aria-label="Back" className="-ml-2 flex size-10 items-center justify-center rounded-full hover:bg-sunken">
            <ArrowLeft className="size-6" />
          </button>
        ) : (
          <img src="/favicon.svg" alt="" className="size-8" />
        )}
        <div className="flex flex-1 gap-1.5">
          {STEPS.map((s, i) => (
            <span key={s} className={cx("h-1.5 flex-1 rounded-full transition", i <= step ? "bg-brand" : "bg-line")} />
          ))}
        </div>
        {skip && (
          <button type="button" onClick={skip} className="px-2 text-sm font-bold text-muted">
            Skip
          </button>
        )}
      </div>
      <div className="mt-6 text-sm font-extrabold uppercase tracking-wide text-brand">
        Step {step + 1} of {STEPS.length}
      </div>
      <h1 className="mt-1 text-3xl font-black tracking-tight">{title}</h1>
      <p className="mt-2 text-muted">{subtitle}</p>
      <div className="mt-6 flex-1">{children}</div>
      <div className="sticky bottom-0 mt-6 bg-canvas pb-1 pt-3">
        <Button size="lg" block onClick={onNext} disabled={nextDisabled} loading={loading}>
          {nextLabel}
        </Button>
      </div>
    </div>
  );
}

function CurrencyStep({ step, next }: { step: number; next: () => void }) {
  const { family } = useSession();
  const rewards = useRewardTypes();
  const cash = rewards.data?.rewardTypes.find((r) => r.kind === "cash");
  const [name, setName] = useState(family.currencyName);
  const [icon, setIcon] = useState(family.currencyIcon);
  const [rate, setRate] = useState(10);
  useEffect(() => {
    if (cash?.pointsPerUnit) setRate(cash.pointsPerUnit);
  }, [cash?.pointsPerUnit]);
  const save = useAction(
    async () => {
      await api.patch("/family", { currencyName: name.trim(), currencyIcon: icon });
      if (cash) await api.patch(`/rewards/${cash.id}`, { pointsPerUnit: rate });
    },
    { onSuccess: next },
  );
  return (
    <StepShell
      step={step}
      title="Pick your family currency"
      subtitle="Kids earn one currency for all their work, then turn it into rewards that matter to them — cash, screen time, or a saved-up goal."
      onNext={() => save.mutate()}
      loading={save.isPending}
      nextDisabled={!name.trim()}
    >
      <div className="grid grid-cols-3 gap-2">
        {CURRENCY_PRESETS.map((c) => (
          <button
            key={c.name}
            type="button"
            onClick={() => {
              setName(c.name);
              setIcon(c.icon);
            }}
            className={cx(
              "flex flex-col items-center gap-1 rounded-2xl p-3 font-extrabold transition",
              name === c.name ? "bg-brand-soft ring-2 ring-brand" : "bg-card shadow-card ring-1 ring-line",
            )}
          >
            <span className="text-3xl">{c.icon}</span>
            {c.name}
          </button>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-[80px_1fr] gap-2">
        <Input aria-label="Currency icon" value={icon} maxLength={4} onChange={(e) => setIcon(e.target.value)} className="text-center text-2xl" />
        <Input aria-label="Currency name" value={name} maxLength={20} onChange={(e) => setName(e.target.value)} />
      </div>
      <Card className="mt-6">
        <Field label={`How many ${name || "points"} make $1?`} hint="This sets the value of everything. 10 is easy math for most kids.">
          <NumberStepper label={`${name || "Points"} per $1`} value={rate} onChange={(v) => setRate(Math.max(1, Math.round(v)))} min={1} max={1000} step={1} />
        </Field>
        <p className="mt-3 text-sm font-bold text-muted">
          {icon} {rate} {name} = $1.00 · {icon} {rate * 5} {name} = $5.00
        </p>
      </Card>
    </StepShell>
  );
}

interface KidForm {
  name: string;
  avatar: string;
  color: string;
  birthYear: number;
  interests: string;
  pin: string;
}

function KidFormCard({
  form,
  setForm,
  editing,
  onSave,
  onCancel,
  saving,
}: {
  form: KidForm;
  setForm: (f: KidForm) => void;
  /** The kid being edited, if any (PIN is optional then) */
  editing?: MemberDTO;
  onSave: () => void;
  onCancel?: () => void;
  saving: boolean;
}) {
  const year = new Date().getFullYear();
  const pinOk = editing && !form.pin ? true : isValidPin(form.pin);
  const valid = !!form.name.trim() && pinOk;
  return (
    <Card className="mt-3 space-y-4">
      <div className="flex items-center gap-3">
        <Avatar member={{ name: form.name, avatar: form.avatar, color: form.color }} size="lg" />
        <TextField label="Name" value={form.name} maxLength={40} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ella" />
      </div>
      <EmojiAvatarPicker value={form.avatar} onChange={(avatar) => setForm({ ...form, avatar })} />
      <div className="flex gap-2">
        {COLORS.map((c) => (
          <button
            key={c}
            type="button"
            aria-label={c}
            onClick={() => setForm({ ...form, color: c })}
            className={cx("size-8 rounded-full", form.color === c && "ring-4 ring-offset-2 ring-offset-card")}
            style={{ background: kidColor(c).bg, ["--tw-ring-color" as string]: kidColor(c).ring }}
          />
        ))}
      </div>
      <Field label="Birth year" hint={`Age ${year - form.birthYear} — helps suggest age-appropriate prices and limits`}>
        <NumberStepper value={form.birthYear} onChange={(v) => setForm({ ...form, birthYear: Math.round(v) })} min={year - 25} max={year} />
      </Field>
      <TextField
        label="Interests (optional)"
        hint="Used only for built-in reward ideas. Please don't include personal details."
        placeholder="Minecraft, soccer, drawing"
        value={form.interests}
        maxLength={300}
        onChange={(e) => setForm({ ...form, interests: e.target.value })}
      />
      <TextField
        label={editing ? "New PIN (optional)" : "PIN (4–6 digits)"}
        inputMode="numeric"
        autoComplete="off"
        value={form.pin}
        maxLength={6}
        onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })}
        hint={editing ? "Leave blank to keep the current PIN." : "Your kid uses this to sign in."}
      />
      <div className="flex gap-2">
        {onCancel && (
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button block onClick={onSave} disabled={!valid} loading={saving}>
          {editing ? `Save ${form.name.trim() || "kid"}` : `Add ${form.name || "kid"}`}
        </Button>
      </div>
    </Card>
  );
}

function KidsStep({ step, next, back }: { step: number; next: () => void; back: () => void }) {
  const { kids } = useSession();
  const year = new Date().getFullYear();
  const blank = (n: number): KidForm => ({ name: "", avatar: AVATARS[n % AVATARS.length], color: COLORS[n % COLORS.length], birthYear: year - 9, interests: "", pin: "" });
  const [form, setForm] = useState(() => blank(kids.length));
  const [adding, setAdding] = useState(kids.length === 0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const editing = editingId ? kids.find((k) => k.id === editingId) : undefined;

  const fields = () => ({
    name: form.name.trim(),
    avatar: form.avatar,
    color: form.color,
    birthYear: form.birthYear,
    interests: form.interests.trim() || null,
  });
  const add = useAction(() => api.post("/family/kids", { ...fields(), pin: form.pin }), {
    success: `${form.name} added!`,
    onSuccess: () => {
      setForm(blank(kids.length + 1));
      setAdding(false);
    },
  });
  const save = useAction((id: string) => api.patch(`/family/kids/${id}`, { ...fields(), ...(form.pin ? { pin: form.pin } : {}) }), {
    success: `${form.name.trim()} saved`,
    onSuccess: () => setEditingId(null),
  });
  const remove = useAction((k: MemberDTO) => api.del(`/family/kids/${k.id}`), { success: "Removed" });

  const startEdit = (k: MemberDTO) => {
    setAdding(false);
    setEditingId(k.id);
    setForm({ name: k.name, avatar: k.avatar, color: k.color, birthYear: k.birthYear ?? year - 9, interests: k.interests ?? "", pin: "" });
  };
  const startAdd = () => {
    setEditingId(null);
    setForm(blank(kids.length));
    setAdding(true);
  };

  return (
    <StepShell
      step={step}
      title="Add your kids"
      subtitle="Just a first name or nickname. Each kid gets a PIN to sign in on devices you pair — no email or contact details needed."
      onBack={back}
      onNext={next}
      nextDisabled={kids.length === 0 || !!editing}
      nextLabel={kids.length ? "Continue" : "Add at least one kid"}
    >
      <div className="space-y-2">
        {kids.map((k) =>
          k.id === editing?.id ? (
            <KidFormCard key={k.id} form={form} setForm={setForm} editing={k} onSave={() => save.mutate(k.id)} onCancel={() => setEditingId(null)} saving={save.isPending} />
          ) : (
            <Card key={k.id} className="flex items-center gap-3 p-3">
              <Avatar member={k} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-extrabold">{k.name}</div>
                <div className="truncate text-sm text-muted">{[k.age && `Age ${k.age}`, k.interests].filter(Boolean).join(" · ")}</div>
              </div>
              <IconButton label={`Edit ${k.name}`} onClick={() => startEdit(k)}>
                <Pencil className="size-5" />
              </IconButton>
              <IconButton
                label={`Remove ${k.name}`}
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Remove ${k.name}? Their profile is deleted.`)) {
                    if (editingId === k.id) setEditingId(null);
                    remove.mutate(k);
                  }
                }}
              >
                <Trash2 className="size-5 text-danger" />
              </IconButton>
            </Card>
          ),
        )}
      </div>
      {adding ? (
        <KidFormCard
          form={form}
          setForm={setForm}
          onSave={() => add.mutate()}
          onCancel={kids.length > 0 ? () => setAdding(false) : undefined}
          saving={add.isPending}
        />
      ) : (
        !editing && (
          <Button variant="secondary" block className="mt-3" onClick={startAdd}>
            <Plus className="size-4" /> Add another kid
          </Button>
        )
      )}
    </StepShell>
  );
}

function ChecklistStep<T extends { title: string; icon: string }>({
  step,
  title,
  subtitle,
  items,
  selected,
  setSelected,
  render,
  onNext,
  back,
  loading,
}: {
  step: number;
  title: string;
  subtitle: string;
  items: T[];
  selected: Set<string>;
  setSelected: (s: Set<string>) => void;
  render?: (item: T) => React.ReactNode;
  onNext: () => void;
  back: () => void;
  loading: boolean;
}) {
  return (
    <StepShell
      step={step}
      title={title}
      subtitle={subtitle}
      onBack={back}
      onNext={onNext}
      loading={loading}
      nextLabel={selected.size ? `Add ${selected.size} & continue` : "Continue"}
      skip={onNext}
    >
      <div className="space-y-2">
        {items.map((item) => {
          const on = selected.has(item.title);
          return (
            <div
              key={item.title}
              className={cx("flex items-center gap-3 rounded-2xl p-3 transition", on ? "bg-brand-soft ring-2 ring-brand" : "bg-card shadow-card ring-1 ring-line")}
            >
              <button
                type="button"
                aria-pressed={on}
                onClick={() => {
                  const s = new Set(selected);
                  if (on) s.delete(item.title);
                  else s.add(item.title);
                  setSelected(s);
                }}
                className="flex flex-1 items-center gap-3 text-left"
              >
                <span className={cx("flex size-7 shrink-0 items-center justify-center rounded-lg", on ? "bg-brand text-on-brand" : "bg-sunken ring-1 ring-line")}>
                  {on && <Check className="size-4" strokeWidth={3} />}
                </span>
                <span className="shrink-0 text-2xl">{item.icon}</span>
                <span className="font-bold">{item.title}</span>
              </button>
              {render?.(item)}
            </div>
          );
        })}
      </div>
    </StepShell>
  );
}

function RulesStep({ step, next, back }: { step: number; next: () => void; back: () => void }) {
  const { family } = useSession();
  const rewards = useRewardTypes();
  const time = rewards.data?.rewardTypes.find((r) => r.kind === "time");
  const [weeklyMinutes, setWeeklyMinutes] = useState(5 * 60);
  const [schoolNights, setSchoolNights] = useState(false);
  const [allowNegative, setAllowNegative] = useState(family.allowNegative);
  const [floor, setFloor] = useState(family.negativeFloor || -50);
  const [workItOff, setWorkItOff] = useState(family.workItOffEnabled);
  const [mode, setMode] = useState(family.responsibilityMode);
  useEffect(() => {
    if (time?.weeklyCap) setWeeklyMinutes(time.weeklyCap);
  }, [time?.weeklyCap]);
  const save = useAction(
    async () => {
      await api.patch("/family", { allowNegative, negativeFloor: allowNegative ? floor : 0, workItOffEnabled: workItOff, responsibilityMode: mode });
      if (time) {
        await api.patch(`/rewards/${time.id}`, {
          weeklyCap: weeklyMinutes,
          schedule: schoolNights
            ? { allowedDays: NOT_SCHOOL_NIGHTS | 1, startTime: null, endTime: time.schedule?.endTime ?? "20:30", note: "No screen time on school nights" }
            : { allowedDays: ALL_DAYS, startTime: null, endTime: time.schedule?.endTime ?? "20:30", note: null },
        });
      }
    },
    { onSuccess: next },
  );
  return (
    <StepShell
      step={step}
      title="Set the ground rules"
      subtitle="Caps keep screen-time rewards healthy. Consequences keep the economy honest. You can change all of this later."
      onBack={back}
      onNext={() => save.mutate()}
      loading={save.isPending}
    >
      {time && (
        <Card className="space-y-4">
          <div className="font-extrabold">
            {time.icon} {time.name}
          </div>
          <Field label="Max per week" hint={`${time.pointsPerUnit} ${family.currencyName} buys 1 minute`}>
            <DurationInput label="Max per week" value={weeklyMinutes} onChange={setWeeklyMinutes} min={0} max={40 * 60} step={30} />
          </Field>
          <Toggle checked={schoolNights} onChange={setSchoolNights} label="No screen time on school nights" description="Earned time can only be used Friday–Sunday." />
        </Card>
      )}
      <Card className="mt-3 space-y-4">
        <div className="font-extrabold">⚖️ Consequences</div>
        <Toggle
          checked={allowNegative}
          onChange={setAllowNegative}
          label="Allow balances below zero"
          description="Deductions can push a kid into the negative, down to a floor you choose."
        />
        {allowNegative && (
          <Field label="Lowest possible balance">
            <NumberStepper value={-floor} onChange={(v) => setFloor(-Math.round(v))} min={0} max={1000} step={10} suffix="below 0" />
          </Field>
        )}
        <Toggle checked={workItOff} onChange={setWorkItOff} label="Work it off" description="Recovery jobs and good-behavior credits help kids climb back to zero." />
      </Card>
      <Card className="mt-3 space-y-3">
        <div className="font-extrabold">✅ Responsibility check-offs</div>
        <Segmented
          value={mode}
          onChange={setMode}
          options={[
            { value: "trust", label: "Trust" },
            { value: "verify", label: "I verify" },
          ]}
        />
        <p className="text-sm text-muted">
          {mode === "trust" ? "Jobs unlock as soon as kids check everything off. You can still undo a check-off." : "Jobs unlock only after you confirm each check-off."}
        </p>
      </Card>
    </StepShell>
  );
}

function ReadyStep({ step, back }: { step: number; back: () => void }) {
  const { me } = useSession();
  const navigate = useNavigate();
  const install = useInstall();
  const qc = useQueryClient();
  const finish = useAction(() => api.patch<MeDTO>("/family", { onboarded: true }), {
    onSuccess: (me) => {
      // Update the session first so the onboarding guard lets us into the dashboard.
      qc.setQueryData(["me"], me);
      navigate("/parent", { replace: true });
    },
  });
  return (
    <StepShell step={step} title="You're all set! 🎉" subtitle="Here's how to get your kids going." onBack={back} onNext={() => finish.mutate()} loading={finish.isPending} nextLabel="Open my dashboard">
      <ConsentCard compact />
      <Card className={me.privacy.consent === "granted" ? "" : "mt-3"}>
        <div className="font-extrabold">🔐 Pair your kids' devices</div>
        <p className="mt-1 text-sm text-muted">
          Kids sign in with their PIN only on devices you pair. Open <b className="text-ink">Settings → Kid devices → Pair a device</b>, then on your
          kid's phone or tablet open this app → “I'm a kid” and enter the code (or scan the QR code).
        </p>
      </Card>
      <Card className="mt-3">
        <div className="font-extrabold">📲 Install on every device</div>
        {install.canPrompt ? (
          <Button size="sm" variant="secondary" className="mt-2" onClick={() => install.prompt()}>
            Install on this device
          </Button>
        ) : (
          <p className="mt-1 text-sm text-muted">
            iPhone/iPad: Share → <b>Add to Home Screen</b>. Android/tablets: browser menu → <b>Install app</b>.
          </p>
        )}
      </Card>
      <Card className="mt-3 text-sm">
        <div className="font-extrabold">What happens next</div>
        <ul className="mt-2 space-y-1.5 text-muted">
          <li>✅ Kids check off responsibilities each day</li>
          <li>🔓 That unlocks paid jobs — they see the reward waiting</li>
          <li>👀 You approve finished work before anything is paid</li>
          <li>💡 Kids can pitch their own jobs anytime</li>
        </ul>
      </Card>
    </StepShell>
  );
}

export function Setup() {
  const { kids, family } = useSession();
  const rewards = useRewardTypes();
  const [step, setStep] = useState(0);
  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const [respSel, setRespSel] = useState(new Set(RESPONSIBILITY_TEMPLATES.slice(0, 4).map((t) => t.title)));
  const createResp = useAction(
    async () => {
      for (const t of RESPONSIBILITY_TEMPLATES.filter((t) => respSel.has(t.title))) {
        await api.post("/responsibilities", { title: t.title, icon: t.icon, frequency: t.frequency, daysOfWeek: t.daysOfWeek ?? 127, dueTime: t.dueTime ?? null });
      }
    },
    { onSuccess: next },
  );

  const cash = rewards.data?.rewardTypes.find((r) => r.kind === "cash");
  const avgAge = useMemo(() => {
    const ages = kids.map((k) => k.age).filter((a): a is number => a !== null);
    return ages.length ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : null;
  }, [kids]);
  const jobTemplates = useMemo(() => JOB_TEMPLATES.filter((t) => !avgAge || !t.minAge || t.minAge <= avgAge + 1), [avgAge]);
  const [jobSel, setJobSel] = useState(new Set(["Wash the car", "Vacuum the living room", "Fold a load of laundry", "Weed the garden"]));
  const [prices, setPrices] = useState<Record<string, number>>({});
  useEffect(() => {
    const rate = cash?.pointsPerUnit ?? 10;
    setPrices(
      Object.fromEntries(
        JOB_TEMPLATES.map((t) => [t.title, quickPrice({ pointsPerDollar: rate, currencyName: family.currencyName }, { title: t.title, estimatedMinutes: t.minutes, age: avgAge }).suggestedReward]),
      ),
    );
  }, [cash?.pointsPerUnit, avgAge, family.currencyName]);
  const createJobs = useAction(
    async () => {
      for (const t of jobTemplates.filter((t) => jobSel.has(t.title))) {
        await api.post("/jobs", { title: t.title, icon: t.icon, reward: prices[t.title] ?? 10, repeat: t.repeat, estimatedMinutes: t.minutes });
      }
    },
    { onSuccess: next },
  );

  return (
    <div className="min-h-dvh bg-canvas">
      {step === 0 && <CurrencyStep step={0} next={next} />}
      {step === 1 && <KidsStep step={1} next={next} back={back} />}
      {step === 2 && (
        <ChecklistStep
          step={2}
          title="Responsibilities"
          subtitle="The non-negotiables. No reward — they're part of being in the family. Paid jobs stay locked until these are done each day."
          items={RESPONSIBILITY_TEMPLATES}
          selected={respSel}
          setSelected={setRespSel}
          onNext={() => (respSel.size ? createResp.mutate() : next())}
          back={back}
          loading={createResp.isPending}
        />
      )}
      {step === 3 && (
        <ChecklistStep
          step={3}
          title="Paid jobs"
          subtitle={`Optional work that earns ${family.currencyName}. Prices are suggested for your kids' ages — tap to adjust.`}
          items={jobTemplates}
          selected={jobSel}
          setSelected={setJobSel}
          render={(t) => (
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label="Lower price"
                className="size-8 rounded-lg bg-sunken font-black"
                onClick={() => setPrices({ ...prices, [t.title]: Math.max(1, (prices[t.title] ?? 10) - 5) })}
              >
                −
              </button>
              <Points value={prices[t.title] ?? 0} family={family} className="w-16 justify-center text-coin-ink" />
              <button
                type="button"
                aria-label="Raise price"
                className="size-8 rounded-lg bg-sunken font-black"
                onClick={() => setPrices({ ...prices, [t.title]: (prices[t.title] ?? 10) + 5 })}
              >
                +
              </button>
            </div>
          )}
          onNext={() => (jobSel.size ? createJobs.mutate() : next())}
          back={back}
          loading={createJobs.isPending}
        />
      )}
      {step === 4 && <RulesStep step={4} next={next} back={back} />}
      {step === 5 && <ReadyStep step={5} back={back} />}
    </div>
  );
}
